/**
 * Curated prompt library — natural-language questions an operator can
 * one-click into their Saved Questions. Covers every MCP tool currently
 * registered in Operate, organized by tool namespace (per
 * `docs/TOOL_RENAME_MAP.md`).
 *
 * Each prompt is phrased so the agentic engine routes to the intended
 * tool without explicit tool naming — the way a real user would ask.
 * `toolsUsed` documents which tools the prompt is designed to exercise.
 *
 * The full reference (with example outputs and the exact tool dispatch
 * each prompt is engineered for) lives at `docs/PROMPT_LIBRARY.md`.
 *
 * Conventions for prompts that embed a Cognos reference:
 *  - Search-paths use Cognos's canonical SINGLE-quote predicate form:
 *    `/content/folder[@name='Sales']/report[@name='Q3 Forecast']`.
 *    Never the double-quoted variant — it doesn't match the content
 *    store and the templates shouldn't teach it.
 *  - For "top reports" / store-wide questions, phrase it so the agent
 *    scopes to the whole content store, not a guessed folder path.
 *  - Don't promise data a tool can't return (e.g. deployment
 *    success/rollback, query-subject-level lineage) — set expectations
 *    to what the backend actually surfaces.
 *  - `question` fields may embed `{{slot.field}}` tokens (e.g.
 *    `{{report.name}}`, `{{report.searchPath}}`, `{{folder.displayPath}}`,
 *    `{{package.name}}`, `{{versionedReport.name}}`,
 *    `{{recycled.storeId}}`, `{{recycled.name}}`). `bindPrompt()`
 *    substitutes these at render time from the backend's live
 *    example-objects map (`Capabilities.examples`), falling back to a
 *    static demo-corpus mirror when nothing has resolved yet. Never
 *    hand-write a literal 'Q3 Forecast' / 'GO Sales (analysis)' /
 *    'Quarterly Sales' / recycle-bin store-id into a new prompt's
 *    `question` — use the matching token instead.
 */

export interface LibraryPrompt {
  /** Stable, dot-namespaced id; used as React key + telemetry tag. */
  id: string;
  /** Tab the prompt appears under in the library dialog. */
  category: PromptCategory;
  /** Short human title for the saved-question name. */
  title: string;
  /** The prompt text — what gets posted to the agent. */
  question: string;
  /** Canonical MCP tool names the prompt is designed to exercise. */
  toolsUsed: string[];
  /** One-line description shown beneath the title in the library. */
  description: string;
  /** When true, included in the "Starter pack" curated set (top 10). */
  starter?: boolean;
  /** Surfaces an "Admin" badge — the underlying tool is admin-gated. */
  requiresAdmin?: boolean;
  /** Surfaces a "Lens" badge — tool needs the metadata-sync replica. */
  requiresLens?: boolean;
  /** Surfaces a "MarkLogic" badge — tool needs the MarkLogic overlay. */
  requiresMarkLogic?: boolean;
  /** Surfaces a "Live SDK" badge — tool needs the Cognos SOAP SDK. */
  requiresCognosSdk?: boolean;
  /** Surfaces a "Writes" badge — tool mutates state; needs MCP_ALLOW_WRITES. */
  isWrite?: boolean;
}

export type PromptCategory =
  | "starter"
  | "content"
  | "activity"
  | "security"
  | "change"
  | "migration"
  | "documentation"
  | "authoring"
  | "visual"
  | "analysis"
  | "performance"
  | "cube"
  | "sdk"
  | "marklogic"
  | "recycle";

export const CATEGORY_LABELS: Record<PromptCategory, string> = {
  starter: "Starter pack",
  content: "Content & Discovery",
  activity: "Activity & Usage",
  security: "Security & Access",
  change: "Change Impact",
  migration: "Query Mode & Migration",
  documentation: "Documentation",
  authoring: "Authoring (write)",
  visual: "Visualizations",
  analysis: "Analysis",
  performance: "Performance",
  cube: "Cube Operations",
  sdk: "Live Cognos SDK",
  marklogic: "MarkLogic Overlay",
  recycle: "Recycle Bin",
};

export const CATEGORY_DESCRIPTIONS: Record<PromptCategory, string> = {
  starter:
    "Curated cross-cutting prompts for new users — the questions that articulate Operate's value in the first 10 minutes.",
  content:
    "Find, browse, and inspect content — reports, dashboards, packages, datasources — across the Cognos content store.",
  activity:
    "Audit-DB-backed analytics: who ran what, when, where. Self-or-admin scoped.",
  security:
    "Permissions, capabilities, group/role membership, effective access. Most calls admin-gated.",
  change:
    "Impact analysis and deployment history — what changed, what depends on it.",
  migration:
    "CQM (legacy) vs DQM (modern) query-mode inventory + per-package CQM→DQM migration readiness. The modernization-gap view: CQM content can't use data modules, dashboards, or explorations.",
  documentation:
    "Auto-generated documentation for reports, packages, data modules.",
  authoring:
    "Create new Cognos content (reports / dashboards / explorations) into a staging folder, then promote it live. Admin + write; OFF unless DATUMCRAFT_AUTHORING_ENABLED=true with a staging folder configured.",
  visual:
    "Render charts, lineage graphs, dependency trees, and other visual artifacts as MCP resource_link outputs.",
  analysis:
    "Adapter-neutral inventory + dependency analytics — every asset, every consumer, every schedule.",
  performance:
    "KPI dashboards, slow-content detection, system-health snapshots.",
  cube:
    "Cube build job inventory + on-demand retry. Some operations require Lens.",
  sdk: "Live Cognos SOAP SDK — content browsing + cube-rebuild trigger. Requires CLOUDCRAFT_COGNOS_SDK_ENABLED=true.",
  marklogic:
    "MarkLogic content-metadata overlay — duplicate detection, orphans, lineage. Requires CLOUDCRAFT_MARKLOGIC_ENABLED=true.",
  recycle:
    "Recycle bin — list, inspect, and recover deleted Cognos content. Read tools require lens; restore requires the SOAP SDK + admin role.",
};

// ─── STARTER PACK (10 cross-cutting prompts new users see first) ────────────

export const STARTER_PROMPTS: LibraryPrompt[] = [
  {
    id: "starter.heaviest-consumer",
    category: "starter",
    title: "Heaviest consumer last month",
    question:
      "Who ran the most reports last month? Show me the top 10 users with their run counts, and break down which folders they pulled from.",
    toolsUsed: ["activity.usageStats", "activity.byFolder"],
    description:
      "The classic 'who's our power user' question. Crosses two activity tools and joins on user id.",
    starter: true,
  },
  {
    id: "starter.failed-runs",
    category: "starter",
    title: "Failed runs this week",
    question:
      "Show me every failed report execution in the last 7 days, grouped by report. Which reports are failing most often, and what are the error messages?",
    toolsUsed: ["activity.failedJobs"],
    description:
      "Surfaces broken content quickly. The agent groups, ranks, and explains.",
    starter: true,
  },
  {
    id: "starter.stale-content",
    category: "starter",
    title: "Stale content (no runs in 90 days)",
    question:
      "Which reports and dashboards haven't been run in the last 90 days? Limit to 50; sort by last-run-date ascending.",
    toolsUsed: ["activity.inactive"],
    description:
      "The seed for a Curate cleanup plan. Often surfaces 30%+ of the content store.",
    starter: true,
  },
  {
    id: "starter.user-perms",
    category: "starter",
    title: "What can user X actually do?",
    question:
      "What groups and roles does user 'jsmith' belong to, and what's their effective permission set across the BI environment?",
    toolsUsed: ["security.groupsAndRoles", "security.effectivePermissions"],
    description:
      "Cross-references group/role membership with effective permissions — the question every IAM auditor asks.",
    starter: true,
  },
  {
    id: "starter.duplicates",
    category: "starter",
    title: "Find similar reports",
    question:
      "Find reports similar to 'Sales Summary Q3 2024' — anything in the last year that looks like a duplicate or near-duplicate. Show me the similarity score and what columns differ.",
    toolsUsed: ["activity.findSimilar"],
    description:
      "The first step toward dedup. Pair with Curate's deduplication plan.",
    starter: true,
  },
  {
    id: "starter.access-explain",
    category: "starter",
    title: "Why can user X see this?",
    question:
      "Explain why user 'amartinez' has access to the 'Finance/Confidential' folder. Walk me through the path — group memberships, capability inheritance, deny overrides.",
    toolsUsed: ["security.explainAccess", "visual.accessPath"],
    description:
      "Compliance teams ask this every audit. Operate gives the path + visualization.",
    starter: true,
  },
  {
    id: "starter.impact",
    category: "starter",
    title: "What depends on this?",
    question:
      "If I retire the 'sales_orders' query subject in the Sales package, which reports and dashboards break? Show me the full impact graph.",
    toolsUsed: ["change.impact", "visual.impact"],
    description:
      "The migration / retirement gate. Operate visualizes downstream consumers.",
    starter: true,
  },
  {
    id: "starter.usage-heatmap",
    category: "starter",
    title: "Usage heatmap by hour",
    question:
      "Show me a heatmap of report execution volume by hour-of-day and day-of-week for the last 30 days. When is our peak load?",
    toolsUsed: ["activity.usageStats", "visual.usageHeatmap"],
    description:
      "Capacity-planning question — frames overnight batch windows + peak-hour scaling.",
    starter: true,
  },
  {
    id: "starter.system-health",
    category: "starter",
    title: "System health right now",
    question:
      "What's the current Cognos dispatcher load, queue depth, and error rate? Anything I should be alarmed about?",
    toolsUsed: ["performance.systemHealth"],
    description:
      "Real-time-ish snapshot — useful for on-call triage during incidents.",
    starter: true,
  },
  {
    id: "starter.orphans",
    category: "starter",
    title: "Orphaned content",
    question:
      "Find content owned by users who no longer exist (deleted, deactivated, or never logged in). Sort by folder so I can hand it to the right successor.",
    toolsUsed: ["content.listOrphaned"],
    description:
      "Common after staff turnover. The output feeds an ownership-transfer plan.",
    starter: true,
    requiresAdmin: true,
  },
];

// ─── CONTENT & DISCOVERY (5) ────────────────────────────────────────────────

export const CONTENT_PROMPTS: LibraryPrompt[] = [
  {
    id: "content.search",
    category: "content",
    title: "Search content by name",
    question:
      "Find every report, dashboard, or query whose name contains 'revenue' — limit to 25, ordered by last modified descending.",
    toolsUsed: ["content.search"],
    description: "Free-text content discovery; the canonical entry point.",
  },
  {
    id: "content.browse",
    category: "content",
    title: "Browse a folder",
    question:
      "List every immediate child of the '{{folder.displayPath}}' folder, with type, owner, and last-modified date.",
    toolsUsed: ["content.browse"],
    description: "One-level folder browse; useful for picking a subfolder before drilling deeper.",
  },
  {
    id: "content.details",
    category: "content",
    title: "Inspect a single report",
    question:
      "Show me everything about the report at search path {{report.searchPath}} — id, owner, package, parameters, output formats, last modified, last run.",
    toolsUsed: ["content.details"],
    description:
      "Deep-dive on a known asset; common before impact analysis or ownership transfer. Search-path uses Cognos's single-quote predicates.",
  },
  {
    id: "content.run-report",
    category: "content",
    title: "Run a report and show the output",
    question:
      "Run the report at {{report.searchPath}} and show me the output rows.",
    toolsUsed: ["content.runReport"],
    description:
      "Executes the report via the Cognos SOAP runner and returns its result rows as a table (preview + full CSV download). Pass parameters for prompted reports.",
    requiresCognosSdk: true,
  },
  {
    id: "content.run-report-pdf",
    category: "content",
    title: "Get a report as a PDF / Excel",
    question:
      "Run the '{{report.name}}' report and give me the rendered PDF document.",
    toolsUsed: ["content.runReport"],
    description:
      "Renders the report in its native format and returns the document as a file — ask for it 'as a PDF' or 'as Excel'.",
    requiresCognosSdk: true,
  },
  {
    id: "content.run-report-chart",
    category: "content",
    title: "Run a report and chart it",
    question:
      "Run the 'Revenue by Region' report and chart revenue by region as a bar chart.",
    toolsUsed: ["content.runReport"],
    description:
      "Executes the report and charts the rows (bar / line / column / pie / area / scatter); axes auto-pick unless you name x / y / series.",
    requiresCognosSdk: true,
  },
  {
    id: "content.lineage",
    category: "content",
    title: "Where does this report's data come from?",
    question:
      "Trace the data lineage for the '{{report.name}}' report — which package and model it's built on, and what else depends on those.",
    toolsUsed: ["lineage.get"],
    description:
      "With Lens, returns the report→package→model dependency chain. Query-subject and database-table level lineage lives inside the FM model spec, not the dependency graph — use the MarkLogic overlay for column-level lineage.",
    requiresLens: true,
  },
  {
    id: "content.list-orphaned",
    category: "content",
    title: "Orphaned content (admin)",
    question:
      "List every content object whose owner has been deactivated or no longer exists. Group by folder so I can route to a successor owner.",
    toolsUsed: ["content.listOrphaned"],
    description: "Admin-only audit; the output typically feeds an ownership-transfer plan in Curate.",
    requiresAdmin: true,
  },
];

// ─── ACTIVITY & USAGE (9) ───────────────────────────────────────────────────

export const ACTIVITY_PROMPTS: LibraryPrompt[] = [
  {
    id: "activity.queryAudit",
    category: "activity",
    title: "Custom audit query",
    question:
      "Show me every report execution attempted by user 'kchen' in the last 14 days, including success/failure status and elapsed time.",
    toolsUsed: ["activity.queryAudit"],
    description: "General-purpose audit query; self-or-admin scoping enforced server-side.",
  },
  {
    id: "activity.usageStats",
    category: "activity",
    title: "Per-asset usage stats",
    question:
      "Across the whole content store, what are the run counts, distinct users, and average run duration for the top 25 reports last month?",
    toolsUsed: ["activity.usageStats"],
    description:
      "The volume / popularity / cost lens on content. Works store-wide (top-N report leaderboard) or scoped to a single report, folder, or package.",
  },
  {
    id: "activity.findSimilar",
    category: "activity",
    title: "Find similar / duplicate content",
    question:
      "Find content similar to 'Monthly Sales Recap' — same package, similar query items, overlapping users. Show similarity score.",
    toolsUsed: ["activity.findSimilar"],
    description: "Dedup discovery — works for any asset kind, not just reports.",
  },
  {
    id: "activity.failedJobs",
    category: "activity",
    title: "Failed jobs in a window",
    question:
      "Pull every failed report or scheduled job in the last 30 days. Group by error code; what are the top 5 failure modes?",
    toolsUsed: ["activity.failedJobs"],
    description: "Reliability triage; the agent often groups + ranks + explains.",
  },
  {
    id: "activity.inactive",
    category: "activity",
    title: "Inactive (stale) content",
    question:
      "Find every report and dashboard that hasn't been executed in the last 180 days. Sort oldest-first; limit to 100.",
    toolsUsed: ["activity.inactive"],
    description: "Cleanup seed; pairs with Curate's archive plan.",
  },
  {
    id: "activity.detail",
    category: "activity",
    title: "Single execution drill-down",
    question:
      "Give me everything about run id 'request-abc-123' — caller, target, elapsed time, parameters, output format, error message if any.",
    toolsUsed: ["activity.detail"],
    description: "Forensic drill-down on a specific incident.",
  },
  {
    id: "activity.byPackage",
    category: "activity",
    title: "Activity for one package",
    question:
      "How is the '{{package.name}}' package being used? Top consumers, top reports built on it, run counts in the last 90 days.",
    toolsUsed: ["activity.byPackage"],
    description: "Package-scoped usage analytics; useful before package retirement decisions.",
  },
  {
    id: "activity.byFolder",
    category: "activity",
    title: "Activity for one folder",
    question:
      "Aggregate runs by report inside the 'Finance/Monthly' folder for the last quarter. Which reports drive the most volume?",
    toolsUsed: ["activity.byFolder"],
    description: "Folder-scoped usage rollup; cleaner than filtering activity.queryAudit by path.",
  },
  {
    id: "activity.logons",
    category: "activity",
    title: "User logon counts",
    question:
      "Who hasn't logged in for the last 60 days? List every account, with last-logon date and total logon count for the period.",
    toolsUsed: ["activity.logons"],
    description: "License-reclaim and dormant-account audits.",
  },
  {
    id: "activity.userReports",
    category: "activity",
    title: "Reports a specific user runs",
    question:
      "Show me every report user 'jsmith' has executed in the last 90 days, with run counts and most-recent execution time. Sort by run count descending.",
    toolsUsed: ["activity.userReports"],
    description:
      "User-centric usage view — surfaces what an analyst actually uses before you reassign their license.",
  },
];

// ─── SECURITY & ACCESS (5) ──────────────────────────────────────────────────

export const SECURITY_PROMPTS: LibraryPrompt[] = [
  {
    id: "security.effectivePermissions",
    category: "security",
    title: "Effective permissions for user X",
    question:
      "Compute the effective permission set for user 'amartinez' — every capability they can exercise, with the source group/role for each.",
    toolsUsed: ["security.effectivePermissions"],
    description:
      "Resolves group + role + deny-override into one flat permission list. Compliance staple.",
  },
  {
    id: "security.usersWithCapability",
    category: "security",
    title: "Who has capability X?",
    question:
      "List every user who has the 'Generate output for any report' capability, including the path that grants it.",
    toolsUsed: ["security.usersWithCapability"],
    description: "Reverse audit — start from a capability, find every grantee. Admin-only.",
    requiresAdmin: true,
  },
  {
    id: "security.explainAccess",
    category: "security",
    title: "Explain user X's access to Y",
    question:
      "Why does user 'jsmith' have read access to 'Confidential/HR Reports'? Walk me through the inheritance path step by step.",
    toolsUsed: ["security.explainAccess"],
    description: "The standard audit-trail explanation; pairs with visual.renderAccessPath.",
  },
  {
    id: "security.groupsAndRoles",
    category: "security",
    title: "Group / role membership",
    question:
      "Which groups and roles does user 'kchen' belong to? Include nested memberships and inherited roles.",
    toolsUsed: ["security.groupsAndRoles"],
    description: "First call in any access investigation.",
  },
  {
    id: "security.systemCapabilities",
    category: "security",
    title: "System-wide capability inventory",
    question:
      "Enumerate every system capability defined in this Cognos install, with a description and which roles grant it.",
    toolsUsed: ["security.systemCapabilities"],
    description: "Useful for reviewing the security model itself, not a specific user.",
    requiresAdmin: true,
  },
];

// ─── CHANGE IMPACT (2) ──────────────────────────────────────────────────────

export const CHANGE_PROMPTS: LibraryPrompt[] = [
  {
    id: "change.deployments",
    category: "change",
    title: "Recent deployments",
    question:
      "Show me every deployment / import-export object touched in the last 30 days — who created or ran it, when, the direction (import vs export), and roughly how many objects it carried.",
    toolsUsed: ["change.deployments"],
    description:
      "Admin-only. Lists deployment specifications from the content store — who and when, not per-run success/rollback (Cognos doesn't keep deployment run history in the content store). Requires Lens.",
    requiresAdmin: true,
    requiresLens: true,
  },
  {
    id: "change.impact",
    category: "change",
    title: "Impact of changing X",
    question:
      "If I rename the 'customer_id' query item in the Sales package, what reports and dashboards would break? Quantify the blast radius.",
    toolsUsed: ["change.impact", "visual.impact"],
    description: "Pre-change impact analysis; pairs with the visualization tool.",
  },
];

// ─── QUERY MODE & MIGRATION (5) ─────────────────────────────────────────────

export const MIGRATION_PROMPTS: LibraryPrompt[] = [
  {
    id: "migration.inventory",
    category: "migration",
    title: "CQM vs DQM inventory",
    question:
      "Give me the query-mode breakdown across the whole content store — how many packages are Compatible Query Mode (CQM, legacy) vs Dynamic Query Mode (DQM), plus the data-module count. I want the size of our modernization gap.",
    toolsUsed: ["querymode.inventory"],
    description:
      "The headline migration number. CQM content can't use data modules, dashboards, or explorations — so the CQM count is the backlog. Lens-backed.",
    requiresLens: true,
  },
  {
    id: "migration.list-cqm",
    category: "migration",
    title: "List the legacy CQM packages",
    question:
      "List our Compatible Query Mode packages — the legacy ones that need migrating to Dynamic Query Mode before we can use data modules and dashboards on them. Show name and folder path.",
    toolsUsed: ["querymode.inventory"],
    description:
      "Surfaces the CQM/MIXED migration candidates by name + path, straight from the FM model spec's queryMode attribute.",
    requiresLens: true,
  },
  {
    id: "migration.assess-one",
    category: "migration",
    title: "Should we migrate this package?",
    question:
      "Assess the CQM→DQM migration readiness for the '{{package.name}}' package — how many reports depend on it, how many are still actively run, what CQM-only blockers (native SQL, stored procedures) exist, and what's the risk?",
    toolsUsed: ["querymode.assessReadiness"],
    description:
      "Per-package assessment: downstream reports, active usage, spec blockers, risk + recommendation. The 'migrate this one?' gate.",
    requiresLens: true,
  },
  {
    id: "migration.prioritize",
    category: "migration",
    title: "Which CQM packages to migrate first",
    question:
      "Inventory our CQM packages, then for the top few by likely usage, assess migration readiness — I want a prioritized list: high-usage low-risk packages to migrate first, unused ones to retire instead.",
    toolsUsed: ["querymode.inventory", "querymode.assessReadiness"],
    description:
      "Two-step migration plan: inventory finds the CQM backlog, readiness ranks it by ROI (usage vs risk). Migrate-vs-retire triage.",
    requiresLens: true,
  },
  {
    id: "migration.retire-candidates",
    category: "migration",
    title: "CQM packages safe to retire",
    question:
      "Which of our CQM packages have NO downstream reports that ran in the last 90 days? Those are retire candidates rather than migrate candidates — confirm before we spend effort converting them.",
    toolsUsed: ["querymode.inventory", "querymode.assessReadiness"],
    description:
      "Separates dead legacy weight from live legacy content — don't migrate what nobody uses; retire it.",
    requiresLens: true,
  },
];

// ─── DOCUMENTATION (2) ──────────────────────────────────────────────────────

export const DOCUMENTATION_PROMPTS: LibraryPrompt[] = [
  {
    id: "documentation.report",
    category: "documentation",
    title: "Auto-document a report",
    question:
      "Generate documentation for the 'Quarterly Revenue Recap' report — purpose, data sources, parameters, output formats, owner, last reviewed.",
    toolsUsed: ["documentation.report"],
    description: "Useful for handoff or compliance reviews.",
  },
  {
    id: "documentation.dataModule",
    category: "documentation",
    title: "Auto-document a data module",
    question:
      "Describe the 'Customer Insights' data module — query subjects, calculated members, source connections, and which reports consume it.",
    toolsUsed: ["documentation.dataModule"],
    description: "The package equivalent of report-doc — covers data modules instead of reports.",
  },
  {
    id: "documentation.summarize-report",
    category: "documentation",
    title: "Summarize a report",
    question:
      "Summarize the 'Quarterly Revenue Recap' report — what package it reads from, the columns and measures it shows, how it's laid out, and what it prompts for.",
    toolsUsed: ["documentation.summarize"],
    description:
      "Distills the report's live spec into the facts you'd mention describing it.",
    requiresCognosSdk: true,
  },
  {
    id: "documentation.summarize-dashboard",
    category: "documentation",
    title: "Summarize a dashboard / exploration",
    question:
      "What does the 'QTD Sales' dashboard show? List its visualizations, the data module(s) behind it, the data items on each widget, and any filters.",
    toolsUsed: ["documentation.summarize"],
    description:
      "Reads the dashboard/exploration JSON spec the SQL sync doesn't mirror, via the live SDK.",
    requiresCognosSdk: true,
  },
];

// ─── AUTHORING (write — create new Cognos content) ───────────────────────────

export const AUTHORING_PROMPTS: LibraryPrompt[] = [
  {
    id: "authoring.report-from-data",
    category: "authoring",
    title: "Author a report from data (staged)",
    question:
      "Run the top 10 reports by run count over the last 90 days, then author a Cognos report named 'Top Reports — 90d' from those results and stage it for review.",
    toolsUsed: ["activity.usageStats", "authoring.report"],
    description:
      "Chains a query into authoring.report — the new report lands in the staging folder for review.",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
  {
    id: "authoring.report-save-current",
    category: "authoring",
    title: "Save the current results as a staged report",
    question:
      "Author a Cognos report named 'Q1 Returns by Region' from the data above and stage it for review.",
    toolsUsed: ["authoring.report"],
    description:
      "Turns the data already in the conversation into a staged Cognos report. (Or use the 'Save as report → Stage for review' button under a chart.)",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
  {
    id: "authoring.suggest-reports",
    category: "authoring",
    title: "Suggest reports for a data module",
    question:
      "Look at the 'Customer 360' data module and suggest a handful of reports worth building from it — for each, the best layout (list / crosstab / chart) and the columns to use, based on the data's nature.",
    toolsUsed: ["documentation.dataModule"],
    description:
      "The agent reads the module's tables/columns (dimensions vs measures) and proposes ready-to-author report ideas. Pick one and it becomes an authorNativeReport call.",
    requiresCognosSdk: true,
  },
  {
    id: "authoring.native-report",
    category: "authoring",
    title: "Author a NATIVE live-query report",
    question:
      "Look at the 'Customer 360' data module, then author a native live-query report named 'Customer 360 — Live' with the customer name, region, and lifetime value columns, and stage it for review.",
    toolsUsed: ["documentation.dataModule", "authoring.nativeReport"],
    description:
      "Builds a REAL report bound to the model (re-runs against live data), not a frozen snapshot. The agent picks the columns from the model. List layout today; charts next.",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
  {
    id: "authoring.promote",
    category: "authoring",
    title: "Promote a staged report live",
    question:
      "Promote the staged report 'Q1 Returns by Region' to the '{{folder.displayPath}}' folder.",
    toolsUsed: ["authoring.promote"],
    description:
      "The review gate — moves a staged object from the sandbox to its real home (optionally renaming).",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
  {
    id: "authoring.exploration",
    category: "authoring",
    title: "Author a dashboard/exploration (experimental)",
    question:
      "Author a Cognos exploration named 'Sales Cockpit' from this spec and stage it for review.",
    toolsUsed: ["authoring.exploration"],
    description:
      "Experimental — needs DATUMCRAFT_AUTHORING_ALLOW_EXPLORATIONS=true and a valid exploration JSON spec; writes only to staging.",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
];

// ─── VISUALIZATIONS (9) ─────────────────────────────────────────────────────

export const VISUAL_PROMPTS: LibraryPrompt[] = [
  {
    id: "visual.lineage",
    category: "visual",
    title: "Lineage graph",
    question:
      "Render a lineage graph for the '{{report.name}}' report — every upstream package, query subject, and source table.",
    toolsUsed: ["lineage.get", "visual.lineage"],
    description: "Graph render of the lineage payload; ships as a resource_link image.",
  },
  {
    id: "visual.impact",
    category: "visual",
    title: "Impact-analysis graph",
    question:
      "Visualize the impact graph if I retire the 'sales_orders' query subject. Highlight reports with > 100 runs/month in red.",
    toolsUsed: ["change.impact", "visual.impact"],
    description: "Same impact data as the textual call, rendered as a downstream-fan graph.",
  },
  {
    id: "visual.accessPath",
    category: "visual",
    title: "Access-path graph",
    question:
      "Visualize the access path that grants user 'jsmith' read access to 'Confidential/HR'. Color the inheritance edges by source.",
    toolsUsed: ["security.explainAccess", "visual.accessPath"],
    description: "The diagram every auditor wants to paste into a SOC2 evidence pack.",
  },
  {
    id: "visual.dependencyTree",
    category: "visual",
    title: "Dependency tree",
    question:
      "Render the full dependency tree rooted at the 'Sales (analysis)' package — every report, dashboard, and downstream module.",
    toolsUsed: ["analysis.objectDependencies", "visual.dependencyTree"],
    description: "Tree view of the structural dependency graph; richer than impact-analysis.",
  },
  {
    id: "visual.usageHeatmap",
    category: "visual",
    title: "Usage heatmap",
    question:
      "Show me a hour-of-day × day-of-week heatmap of report executions for the last 60 days.",
    toolsUsed: ["activity.usageStats", "visual.usageHeatmap"],
    description: "Capacity-planning view; identifies peak windows.",
  },
  {
    id: "visual.folderTree",
    category: "visual",
    title: "Folder tree",
    question:
      "Render the folder tree for 'Public Folders/Finance' down to 3 levels deep, with object counts at each node.",
    toolsUsed: ["content.browse", "visual.folderTree"],
    description: "Visual content-store overview; useful for stakeholder presentations.",
  },
  {
    id: "visual.groupHierarchy",
    category: "visual",
    title: "Group / role hierarchy",
    question:
      "Render the group + role hierarchy for the 'Finance' namespace, showing which roles each group inherits.",
    toolsUsed: ["security.groupsAndRoles", "visual.groupHierarchy"],
    description: "Visual security-model overview.",
    requiresAdmin: true,
  },
  {
    id: "visual.dataModuleSchema",
    category: "visual",
    title: "Data-module schema diagram",
    question:
      "Render the schema diagram for the 'Customer Insights' data module — query subjects, joins, calculated members.",
    toolsUsed: ["documentation.dataModule", "visual.dataModuleSchema"],
    description: "ERD-style schema view; pairs with documentation.dataModule output.",
  },
  {
    id: "visual.chart",
    category: "visual",
    title: "Inline chart from result",
    question:
      "Show me top-10 reports by run count last month as a horizontal bar chart.",
    toolsUsed: ["activity.usageStats", "visual.chart"],
    description: "Auto-charts a tabular result. The agent picks bar / line / pie.",
  },
  {
    id: "visual.versionTimeline",
    category: "visual",
    title: "Version timeline",
    question:
      "Render a timeline of every saved version of the '{{versionedReport.name}}' report — show who edited each version, when, and visually flag the largest spec deltas.",
    toolsUsed: ["analysis.objectVersionHistory", "visual.versionTimeline"],
    description:
      "Horizontal timeline view of report version history; pairs with compareObjectVersions for drill-down.",
  },
];

// ─── ANALYSIS (9) ───────────────────────────────────────────────────────────

export const ANALYSIS_PROMPTS: LibraryPrompt[] = [
  {
    id: "analysis.objectInventory",
    category: "analysis",
    title: "Object inventory",
    question:
      "Inventory every object in the BI environment, filtered to type=report, owner=Finance, modified-since=2026-01-01. Group by classification.",
    toolsUsed: ["analysis.objectInventory"],
    description: "Adapter-neutral inventory; flexible filters.",
  },
  {
    id: "analysis.securityProfile",
    category: "analysis",
    title: "Composite security profile",
    question:
      "Build a complete security profile for user 'amartinez' — effective permissions + group memberships + inherited capabilities + any deny overrides — in one consolidated view.",
    toolsUsed: ["analysis.securityProfile"],
    description: "Composite view; cheaper than calling 4 individual security tools.",
  },
  {
    id: "analysis.packageStructure",
    category: "analysis",
    title: "Package internals",
    question:
      "Show me the internals of the '{{package.name}}' package — query subjects, query items, dimensions, calculated members, and data source connections.",
    toolsUsed: ["analysis.packageStructure"],
    description: "FM modeller view of a package; useful before refactoring.",
  },
  {
    id: "analysis.databaseUsage",
    category: "analysis",
    title: "Database / schema usage",
    question:
      "What databases and schemas does this BI environment read from? For each one, list the assets that depend on it.",
    toolsUsed: ["analysis.databaseUsage"],
    description: "Storage + DB-cost attribution; the upstream-warehouse view.",
  },
  {
    id: "analysis.scheduleInventory",
    category: "analysis",
    title: "Schedule inventory",
    question:
      "List every active, disabled, and failing schedule in the system. For each, owner, frequency, and next-run timestamp.",
    toolsUsed: ["analysis.scheduleInventory"],
    description: "Cron-style schedule view; spot disabled-but-not-deleted schedules.",
  },
  {
    id: "analysis.outputArchive",
    category: "analysis",
    title: "Output archive (saved outputs)",
    question:
      "Show me the saved-output archive for 'Quarterly Revenue Recap' — every output version generated, recipients, retention status.",
    toolsUsed: ["analysis.outputArchive"],
    description: "Compliance-grade record of past report deliveries.",
  },
  {
    id: "analysis.objectDependencies",
    category: "analysis",
    title: "Full object-dependency graph",
    question:
      "Build the full structural dependency graph for the 'Sales' package — including parameters, query items, drill paths.",
    toolsUsed: ["analysis.objectDependencies"],
    description: "Richer than change.impact; includes structural items, not just consumer assets.",
  },
  {
    id: "analysis.dataSourceDependencies",
    category: "analysis",
    title: "Data-source consumers",
    question:
      "Every consumer of the 'GO_Sales_DW' data source, broken down by usage frequency. Top 20.",
    toolsUsed: ["analysis.dataSourceDependencies"],
    description: "Reverse data-source map; useful before retiring a connection.",
  },
  {
    id: "analysis.modelItemDependencies",
    category: "analysis",
    title: "Model-item dependencies (FM)",
    question:
      "For each query subject in the 'Sales' Framework Manager model, list the consuming reports and packages. Flag unused items.",
    toolsUsed: ["analysis.modelItemDependencies"],
    description: "The FM modeller's pruning view — find unused query items.",
  },
  {
    id: "analysis.objectRunHistory",
    category: "analysis",
    title: "Report run history",
    question:
      "Show me every execution of report '{{report.name}}' in the last 6 months — when, by whom, success or failure, and total runtime.",
    toolsUsed: ["analysis.objectRunHistory"],
    description:
      "Per-object slice of the audit run table. Distinct from getReportOutputHistory which lists saved output artifacts.",
  },
  {
    id: "analysis.reportOutputHistory",
    category: "analysis",
    title: "Saved report outputs",
    question:
      "List every saved output artifact (HTML, PDF, Excel, CSV) for the 'Sales Summary' report — when each was generated, by whom, and the storeId so I can fetch the bytes.",
    toolsUsed: ["analysis.reportOutputHistory"],
    description:
      "Tracks the artifacts a report produced, not the spec edits. Pairs with the dispatcher for byte retrieval.",
  },
  {
    id: "analysis.objectVersionHistory",
    category: "analysis",
    title: "Report version history",
    question:
      "Show me the saved-spec version history for report 'Pension Plan' — every version retained, who edited it, when, and whether the spec XML is available for diffing.",
    toolsUsed: ["analysis.objectVersionHistory"],
    description:
      "Cognos's <class>Version child mechanism — covers report / interactiveReport / query / analysis. The setup for compareObjectVersions.",
  },
  {
    id: "analysis.compareObjectVersions",
    category: "analysis",
    title: "Diff two report versions",
    question:
      "Diff version 3 vs version 4 of the '{{versionedReport.name}}' report — what changed at the query / data item / filter / drill-link level?",
    toolsUsed: ["analysis.objectVersionHistory", "analysis.compareObjectVersions"],
    description:
      "Semantic version diff. Pulls cmids from version history first, then compares spec XML.",
  },
  {
    id: "analysis.objectSnapshotHistory",
    category: "analysis",
    title: "Spec snapshot history",
    question:
      "List the daily spec snapshots captured for report 'Customer Insights' over the last 30 days. Which days actually changed?",
    toolsUsed: ["analysis.objectSnapshotHistory"],
    description:
      "Lens-side snapshot trail for classes whose version history Cognos overwrites in place (queries, analyses).",
    requiresLens: true,
  },
  {
    id: "analysis.compareObjectSnapshots",
    category: "analysis",
    title: "Diff two spec snapshots",
    question:
      "Compare yesterday's and today's spec snapshot for the 'Daily Audit Summary' query. What query items changed?",
    toolsUsed: ["analysis.objectSnapshotHistory", "analysis.compareObjectSnapshots"],
    description:
      "Pairs with snapshot history — same diff machinery as compareObjectVersions, against the lens snapshot store.",
    requiresLens: true,
  },
  {
    id: "analysis.captureSpecSnapshots",
    category: "analysis",
    title: "Trigger a spec-snapshot sweep",
    question:
      "Force a fresh spec-snapshot capture across the BI environment right now — I'm about to redeploy and want a baseline.",
    toolsUsed: ["analysis.captureSpecSnapshots"],
    description:
      "Admin / write — triggers the snapshot batch out-of-band from the scheduled run.",
    requiresAdmin: true,
    requiresLens: true,
    isWrite: true,
  },
  {
    id: "analysis.saveVizAsCognosReport",
    category: "analysis",
    title: "Save a viz as a Cognos report",
    question:
      "Save the chart I'm looking at — the 'Top 10 reports by run count' viz — as a Cognos report named 'Top reports — snapshot 2026Q1' under /content/folder[@name='My Folders']/folder[@name='Snapshots'].",
    toolsUsed: ["analysis.saveVizAsCognosReport"],
    description:
      "Admin / write — persists the current viz as a real Cognos report. Pair with the chat's 'Save as report' button for click-based execution.",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
];

// ─── PERFORMANCE (6) ────────────────────────────────────────────────────────

export const PERFORMANCE_PROMPTS: LibraryPrompt[] = [
  {
    id: "performance.summary",
    category: "performance",
    title: "KPI summary",
    question:
      "Give me the performance KPI summary — totalMetrics, slowContent count, average response time, system health score.",
    toolsUsed: ["performance.summary"],
    description: "Top-of-funnel performance dashboard.",
  },
  {
    id: "performance.contentMetrics",
    category: "performance",
    title: "Per-asset performance metrics",
    question:
      "What's the load-time distribution (p50, p95, p99) for the 'Quarterly Revenue' report over the last 14 days? Include run counts and error rate.",
    toolsUsed: ["performance.contentMetrics"],
    description: "Per-asset latency + error analytics.",
  },
  {
    id: "performance.slowContent",
    category: "performance",
    title: "Slow-content detection",
    question:
      "Find every report whose p95 run time exceeds 30 seconds in the last 7 days. Sort slowest first.",
    toolsUsed: ["performance.slowContent"],
    description: "The 'optimize these first' shortlist.",
  },
  {
    id: "performance.systemHealth",
    category: "performance",
    title: "System-health snapshot",
    question:
      "Snapshot dispatcher load, memory pressure, queue depth, and error spikes right now. Anything I should be alerting on?",
    toolsUsed: ["performance.systemHealth"],
    description: "Real-time-ish health check; useful during on-call triage.",
  },
  {
    id: "performance.collectMetric",
    category: "performance",
    title: "Trigger an on-demand metric collection",
    question:
      "Run a one-off performance collection on the 'Quarterly Revenue' report and report back when it's done.",
    toolsUsed: ["performance.collectMetric"],
    description: "Admin-only write; updates the metrics store on demand.",
    requiresAdmin: true,
    isWrite: true,
  },
  {
    id: "performance.collectSystemHealth",
    category: "performance",
    title: "Trigger a system-health snapshot",
    question:
      "Trigger an on-demand system-health snapshot. I want a fresh reading, not the cached one.",
    toolsUsed: ["performance.collectSystemHealth"],
    description: "Admin-only write; forces a fresh collection vs cached snapshot.",
    requiresAdmin: true,
    isWrite: true,
  },
];

// ─── CUBE OPERATIONS (4) ────────────────────────────────────────────────────

export const CUBE_PROMPTS: LibraryPrompt[] = [
  {
    id: "cognos.cube.listJobs",
    category: "cube",
    title: "List cube build jobs",
    question:
      "List every cube build job — name, schedule, last run status, last run timestamp. Highlight any that have been failing for more than 24 hours.",
    toolsUsed: ["cognos.cube.listJobs"],
    description: "Cube job inventory; foundation for cube triage.",
    requiresLens: true,
  },
  {
    id: "cognos.cube.buildHealth",
    category: "cube",
    title: "Cube build health",
    question:
      "What's the health of the cube build pipeline this week? Success rate, average build duration, failure modes.",
    toolsUsed: ["cognos.cube.buildHealth"],
    description: "Aggregate health view of the cube subsystem.",
    requiresLens: true,
  },
  {
    id: "cognos.cube.findStaleJobs",
    category: "cube",
    title: "Stale scheduled jobs",
    question:
      "Find any scheduled cube build job that hasn't fired successfully in the last 14 days, even though it's still active.",
    toolsUsed: ["cognos.cube.findStaleJobs"],
    description: "Detects schedules silently broken — common cause of stale dashboards.",
    requiresLens: true,
  },
  {
    id: "cognos.cube.retryBuild",
    category: "cube",
    title: "Retry a failed cube build",
    question:
      "The 'Sales_Forecast' cube build failed last night. Retry it and tell me when it succeeds or fails again.",
    toolsUsed: ["cognos.cube.retryBuild"],
    description: "Admin-only write — requires MCP_ALLOW_WRITES + Cognos SDK enabled.",
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
];

// ─── LIVE COGNOS SDK (3) ────────────────────────────────────────────────────

export const SDK_PROMPTS: LibraryPrompt[] = [
  {
    id: "cognos.sdk.browseContent",
    category: "sdk",
    title: "Live content browse via SOAP",
    question:
      "Browse the children of /content/folder[@name='Live Demo'] using the live Cognos SDK (not the audit-DB cache).",
    toolsUsed: ["cognos.sdk.browseContent"],
    description: "Live-vs-cached browse; bypass the audit DB.",
    requiresCognosSdk: true,
  },
  {
    id: "cognos.sdk.listGroupMembers",
    category: "sdk",
    title: "Live group-member list",
    question:
      "List every member of the 'Finance Analysts' group via the live SDK — direct members + nested group members.",
    toolsUsed: ["cognos.sdk.listGroupMembers"],
    description: "Live group walk; reflects current state, not cache.",
    requiresCognosSdk: true,
  },
  {
    id: "cognos.sdk.listPrincipalMemberships",
    category: "sdk",
    title: "Live principal memberships",
    question:
      "Show me every group and role 'amartinez' belongs to via the live SDK, including indirect/nested memberships.",
    toolsUsed: ["cognos.sdk.listPrincipalMemberships"],
    description: "Live membership reverse-lookup; mirrors security.groupsAndRoles via SDK.",
    requiresCognosSdk: true,
  },
];

// ─── MARKLOGIC OVERLAY (6) ──────────────────────────────────────────────────

export const MARKLOGIC_PROMPTS: LibraryPrompt[] = [
  {
    id: "cognos.ml.ping",
    category: "marklogic",
    title: "MarkLogic health check",
    question:
      "Is the MarkLogic overlay reachable? Run a ping and report back with version + connection latency.",
    toolsUsed: ["cognos.ml.ping"],
    description: "Sanity check before any other ML* call.",
    requiresMarkLogic: true,
  },
  {
    id: "cognos.ml.inventory",
    category: "marklogic",
    title: "MarkLogic object inventory",
    question:
      "Pull the MarkLogic-side object inventory — every report and dashboard the overlay knows about, with metadata richer than the audit DB carries.",
    toolsUsed: ["cognos.ml.inventory"],
    description: "ML-overlay-specific inventory; usually deeper metadata than the audit DB.",
    requiresMarkLogic: true,
  },
  {
    id: "cognos.ml.lineage",
    category: "marklogic",
    title: "MarkLogic-grade lineage",
    question:
      "Trace the full data lineage for '{{report.name}}' through the MarkLogic overlay — including upstream warehouse columns.",
    toolsUsed: ["cognos.ml.lineage"],
    description: "Deeper lineage than what the audit DB exposes; column-level when ML has it.",
    requiresMarkLogic: true,
  },
  {
    id: "cognos.ml.findOrphans",
    category: "marklogic",
    title: "MarkLogic orphan detection",
    question:
      "Use the MarkLogic overlay to find orphaned reports — owners that no longer exist, packages that have been deleted.",
    toolsUsed: ["cognos.ml.findOrphans"],
    description: "Cross-references audit DB with ML metadata; usually finds more orphans than content.listOrphaned alone.",
    requiresMarkLogic: true,
  },
  {
    id: "cognos.ml.findDuplicates",
    category: "marklogic",
    title: "MarkLogic duplicate detection",
    question:
      "Use the MarkLogic overlay to find duplicate reports — same query items, same package, similar names.",
    toolsUsed: ["cognos.ml.findDuplicates"],
    description: "ML-grade duplicate detection; semantically richer than activity.findSimilar.",
    requiresMarkLogic: true,
  },
  {
    id: "cognos.ml.runHistory",
    category: "marklogic",
    title: "MarkLogic run history",
    question:
      "Pull the MarkLogic-overlay run history for 'Quarterly Revenue Recap' — every execution, with parameters and outcomes.",
    toolsUsed: ["cognos.ml.runHistory"],
    description: "ML-overlay execution log; complementary to activity.queryAudit.",
    requiresMarkLogic: true,
  },
];

// ─── RECYCLE BIN (5) ────────────────────────────────────────────────────────

export const RECYCLE_PROMPTS: LibraryPrompt[] = [
  {
    id: "recycle.list-recent",
    category: "recycle",
    title: "What got deleted recently?",
    question:
      "Show me everything in the Cognos recycle bin that was deleted in the last 7 days. Group by who deleted, sort by most-recent first.",
    toolsUsed: ["cognos.recycle.list"],
    description: "First call when investigating a 'who deleted my report?' incident.",
    requiresLens: true,
  },
  {
    id: "recycle.list-by-user",
    category: "recycle",
    title: "Track a specific user's deletes",
    question:
      "Show me everything user 'jsmith' has deleted in the last 30 days. Include past-retention items so I can see what's already beyond recovery.",
    toolsUsed: ["cognos.recycle.list"],
    description: "Per-user audit; useful before offboarding or after a suspicious-activity flag.",
    requiresLens: true,
  },
  {
    id: "recycle.list-folder-scope",
    category: "recycle",
    title: "Deletes inside a folder",
    question:
      "What's been deleted from the 'Public Folders/Finance' folder tree in the last quarter? Recoverable only.",
    toolsUsed: ["cognos.recycle.list"],
    description: "Folder-scoped recycle inventory; common before reviewing a team's content hygiene.",
    requiresLens: true,
  },
  {
    id: "recycle.details",
    category: "recycle",
    title: "Inspect a deleted object",
    question:
      "Tell me everything about deleted store-id {{recycled.storeId}} — when was it deleted, by whom, can it be restored, and is the original parent still available?",
    toolsUsed: ["cognos.recycle.details"],
    description: "Pre-flight inspection before any restore. Surfaces collisions and parent-purge blockers.",
    requiresLens: true,
  },
  {
    id: "recycle.deep-deletes",
    category: "recycle",
    title: "Find past-retention items",
    question:
      "Show me items in the recycle bin that are past their retention window — already beyond recovery through Operate, but I want to know what's there before they're purged.",
    toolsUsed: ["cognos.recycle.list"],
    description: "Surfaces the 'almost gone' set — useful for backup-recovery planning.",
    requiresLens: true,
  },
  {
    id: "recycle.restore-single",
    category: "recycle",
    title: "Restore one deleted report",
    question:
      "Restore the deleted report with store-id {{recycled.storeId}} back to its original location. Reason: Accidentally deleted by jsmith on 2026-05-04 per ticket OPS-3421.",
    toolsUsed: ["cognos.recycle.details", "cognos.recycle.restore"],
    description: "Single-item restore. The agent should run details first to surface blockers, then restore.",
    requiresLens: true,
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
  {
    id: "recycle.restore-with-rename",
    category: "recycle",
    title: "Restore with a new name",
    question:
      "Restore deleted store-id {{recycled.storeId}} with a new name '{{recycled.name}} (restored)' — there's already a live report at the original target. Reason: Restoring older version per OPS-3421.",
    toolsUsed: ["cognos.recycle.restore"],
    description: "Resolves a name collision via alternateName; uses Cognos's moveRename SOAP call.",
    requiresLens: true,
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
  {
    id: "recycle.restore-bulk",
    category: "recycle",
    title: "Bulk restore everything jsmith deleted yesterday",
    question:
      "Find every report jsmith deleted yesterday and restore them all back to their original locations. Reason: Accidental bulk delete per ticket OPS-3421. Use the bulk-restore tool so I get per-item success/failure feedback.",
    toolsUsed: ["cognos.recycle.list", "cognos.recycle.restoreBulk"],
    description: "Two-step: list to find the storeIds, bulk-restore to lift them back. NOT transactional — per-item outcomes.",
    requiresLens: true,
    requiresAdmin: true,
    requiresCognosSdk: true,
    isWrite: true,
  },
];

// ─── EXPORT — combined library + grouping helpers ───────────────────────────

export const ALL_PROMPTS: LibraryPrompt[] = [
  ...STARTER_PROMPTS,
  ...CONTENT_PROMPTS,
  ...ACTIVITY_PROMPTS,
  ...SECURITY_PROMPTS,
  ...CHANGE_PROMPTS,
  ...MIGRATION_PROMPTS,
  ...DOCUMENTATION_PROMPTS,
  ...AUTHORING_PROMPTS,
  ...VISUAL_PROMPTS,
  ...ANALYSIS_PROMPTS,
  ...PERFORMANCE_PROMPTS,
  ...CUBE_PROMPTS,
  ...SDK_PROMPTS,
  ...MARKLOGIC_PROMPTS,
  ...RECYCLE_PROMPTS,
];

export const PROMPT_CATEGORIES: PromptCategory[] = [
  "starter",
  "content",
  "activity",
  "security",
  "change",
  "migration",
  "documentation",
  "authoring",
  "visual",
  "analysis",
  "performance",
  "cube",
  "sdk",
  "marklogic",
  "recycle",
];

/**
 * Group prompts by category for display in the library dialog. Stable
 * insertion order matches the {@link PROMPT_CATEGORIES} constant.
 */
export function groupByCategory(): Record<PromptCategory, LibraryPrompt[]> {
  const out = Object.fromEntries(
    PROMPT_CATEGORIES.map((c) => [c, [] as LibraryPrompt[]]),
  ) as Record<PromptCategory, LibraryPrompt[]>;
  for (const p of ALL_PROMPTS) out[p.category].push(p);
  return out;
}
